import {computed, effect, inject, Service, signal, untracked} from '@angular/core';
import {defer, forkJoin, map, Observable, of, shareReplay, switchMap, tap} from 'rxjs';
import {REPOSITORY_DASHBOARD_PREFERENCES_TOKEN} from '../../../adapters/implementation/providers/tokens-injections';
import {DashboardPreferencesRepository} from '../../ports/restitution/dashboard-preferences.port';
import {CustomerJourneyService} from '../customer-journey/customer-journey.service';
import {LicenseService} from '../company/license.service';
import {LicenseWithStatus} from '../company/model/license.with.status.model';
import {SurveyHeader} from '../surveys/model/survey.header.model';
import {SurveyService} from '../surveys/survey.service';
import {SurveyType} from '../surveys/model/survey.type.model';
import {isSameDashboardScope} from './is-same-dashboard-scope';
import {DashboardScope} from './model/dashboard-scope.model';
import {DashboardScopeOption} from './model/dashboard-scope-option.model';
import {resolveDashboardScope} from './resolve-dashboard-scope';
import {toDashboardScopeOptions} from './to-dashboard-scope-options';

/**
 * Seules ces enquêtes se restituent : un formulaire ne mesure pas la satisfaction et n'a donc ni
 * NPS, ni CSAT, ni verbatim à analyser.
 */
const RESTITUABLE_SURVEY_TYPES: SurveyType[] = [SurveyType.SATISFACTION, SurveyType.SATISFACTION_STAGES];

/**
 * Contexte des pages de restitution : la cible regardée — une enquête ou un parcours client —
 * et les cibles au choix.
 *
 * Un seul endroit le détient. Le sélecteur de l'entête l'ouvre et y pose les choix
 * (cf. `HeaderContentDashboard`), les pages s'y contentent de lire. Elles ne se parlent pas entre
 * elles, ce qui évite la circulation à double sens de l'ancienne application, où chaque page
 * chargeait la liste pour la repasser à l'entête, qui lui renvoyait ensuite la sélection.
 */
@Service()
export class DashboardScopeService {
    private readonly repository: DashboardPreferencesRepository = inject(REPOSITORY_DASHBOARD_PREFERENCES_TOKEN);
    private readonly licenseService = inject(LicenseService);
    private readonly customerJourneyService = inject(CustomerJourneyService);
    private readonly surveyService = inject(SurveyService);

    /**
     * Annoncer la même cible ne réveille pas les pages : sans ce comparateur, chaque annonce poserait
     * un objet neuf et leur ferait recharger une restitution qui n'a pas changé.
     */
    private readonly scopeSignal = signal<DashboardScope | null>(null, {equal: isSameDashboardScope});

    /** Toutes les cibles accessibles, avant que la page regardée n'en écarte (cf. `restrictToSurveys`). */
    private readonly allOptionsSignal = signal<DashboardScopeOption[]>([]);
    private readonly surveysOnlySignal = signal(false);
    private readonly loadingSignal = signal(true);
    private readonly surveyInJourneySignal = signal<number | null>(null);
    private readonly stageInSurveySignal = signal<number | null>(null);
    private readonly surveyHeaderSignal = signal<SurveyHeader | null>(null);

    /**
     * L'abonnement ne change pas en cours de session : il n'est demandé qu'à la première lecture de
     * la liste, puis rejoué. Le relire à chaque relecture ferait un aller-retour pour une réponse
     * déjà connue.
     */
    private readonly license$: Observable<LicenseWithStatus> = defer(() =>
        this.licenseService.findLicenseWithStatus()
    ).pipe(shareReplay({bufferSize: 1, refCount: false}));

    /** Cible regardée ; `null` si la société n'a aucune cible à restituer. */
    public readonly scope = this.scopeSignal.asReadonly();

    /**
     * Cibles au choix : les parcours clients puis les enquêtes, chaque groupe par ordre alphabétique.
     *
     * Les seules enquêtes quand la page regardée ne sait pas lire un parcours (cf. `restrictToSurveys`).
     */
    public readonly options = computed(() =>
        this.surveysOnlySignal()
            ? this.allOptionsSignal().filter((option) => option.kind === 'SURVEY')
            : this.allOptionsSignal()
    );

    /** Vrai tant que la page regardée ne propose que des enquêtes. */
    public readonly surveysOnly = this.surveysOnlySignal.asReadonly();

    /**
     * Enquête regardée à l'intérieur du parcours ; `null` quand c'est le parcours entier.
     *
     * N'a de sens que sur un parcours, et retombe à `null` dès qu'on change de cible.
     */
    public readonly surveyInJourney = this.surveyInJourneySignal.asReadonly();

    /**
     * Étape regardée à l'intérieur de l'enquête ; `null` quand c'est l'enquête entière.
     *
     * N'a de sens que sur une enquête à étapes, et retombe à `null` dès qu'on change de cible ou
     * d'enquête : une étape n'existe que dans l'enquête qui la porte.
     */
    public readonly stageInSurvey = this.stageInSurveySignal.asReadonly();

    /**
     * Entête de l'enquête dont les données sont lues ; `null` sur un parcours entier.
     *
     * Elle porte le type de l'enquête et ses étapes. Elle est lue ici, une fois, plutôt que par
     * chaque écran qui en a besoin : le sous-menu des étapes et la vue globale la demandaient tous
     * deux, et la page se chargeait visiblement deux fois.
     */
    public readonly surveyHeader = this.surveyHeaderSignal.asReadonly();

    /**
     * Cible dont les données sont lues : l'enquête ouverte dans le parcours, ou la cible elle-même.
     *
     * À distinguer de `scope`, qui reste ce que le sélecteur de l'entête désigne et ce à quoi les
     * filtres se rattachent.
     */
    public readonly dataScope = computed<DashboardScope | null>(() => {
        const idSurvey = this.surveyInJourneySignal();

        return idSurvey ? {kind: 'SURVEY', id: idSurvey} : this.scopeSignal();
    });

    /**
     * Vrai tant que les cibles n'ont pas été lues une première fois.
     *
     * Il faut le consulter avant de conclure de `scope()` : une cible nulle ne veut dire « aucune
     * enquête à restituer » qu'une fois le chargement terminé.
     */
    public readonly loading = this.loadingSignal.asReadonly();

    public constructor() {
        effect(() => {
            const dataScope = this.dataScope();

            // `untracked` : le chargement pose l'entête, que d'autres calculs lisent — sans quoi
            // l'effet se rappellerait sur sa propre écriture.
            untracked(() => {
                if (dataScope?.kind !== 'SURVEY') {
                    this.surveyHeaderSignal.set(null);
                    return;
                }

                this.surveyService.findHeader(dataScope.id).subscribe((header) => this.surveyHeaderSignal.set(header));
            });
        });
    }

    /**
     * Ouvre les restitutions : lit les cibles, détermine celle à regarder, l'annonce et l'enregistre
     * si elle diffère de celle du compte.
     *
     * @param fromUrl cible désignée par l'URL, prioritaire sur celle du compte.
     */
    public readonly open = (fromUrl: DashboardScope | null): Observable<void> =>
        forkJoin({options: this.findOptions(), working: this.repository.getWorkingScope()}).pipe(
            tap(() => this.loadingSignal.set(false)),
            switchMap(({options, working}) => this.retain(options, fromUrl, working))
        );

    /**
     * Relit les cibles en conservant celle qu'on regarde.
     *
     * Une enquête créée, renommée ou supprimée ailleurs — dans un autre onglet, par un collègue —
     * se retrouve ainsi dans la liste sans recharger l'application. Si celle qu'on regardait a
     * disparu, la suivante prend sa place.
     */
    public readonly refresh = (): Observable<void> =>
        this.findOptions().pipe(switchMap((options) => this.retain(options, null, this.scopeSignal())));

    /**
     * Retient un choix de l'utilisateur : l'annonce aux pages sans attendre, puis l'enregistre.
     *
     * L'annonce est immédiate et ne dépend pas du réseau — un enregistrement en échec ne coûte que
     * la mémoire du choix au prochain passage, pas la consultation en cours.
     */
    public readonly select = (scope: DashboardScope | null): Observable<void> => {
        this.announceScope(scope);

        return this.repository.saveWorkingScope(scope);
    };

    public readonly announceScope = (scope: DashboardScope | null): void => {
        // Changer de cible referme la vue ouverte sur une enquête du parcours précédent.
        if (!isSameDashboardScope(scope, this.scopeSignal())) {
            this.surveyInJourneySignal.set(null);
            this.stageInSurveySignal.set(null);
        }

        this.scopeSignal.set(scope);
    };

    /**
     * Regarde une enquête à l'intérieur du parcours, ou le parcours entier avec `null`.
     *
     * Cela ne change pas la cible : les filtres restent ceux du parcours, et c'est encore lui que le
     * sélecteur de l'entête affiche. Seules les données lues se resserrent sur l'enquête.
     */
    public readonly selectSurveyInJourney = (idSurvey: number | null): void => {
        this.surveyInJourneySignal.set(this.scopeSignal()?.kind === 'CUSTOMER_JOURNEY' ? idSurvey : null);
        // Changer d'enquête change d'étapes : celle qu'on regardait n'existe plus ici.
        this.stageInSurveySignal.set(null);
    };

    /** Regarde une étape à l'intérieur de l'enquête, ou l'enquête entière avec `null`. */
    public readonly selectStageInSurvey = (idStage: number | null): void => this.stageInSurveySignal.set(idStage);

    /**
     * Réserve les cibles aux enquêtes, ou rouvre les parcours clients avec `false`.
     *
     * Pour une page qui ne sait lire qu'une enquête : un parcours regardé jusque-là cède alors la
     * place à une enquête, retenue comme le serait une cible disparue de la liste. Les autres pages
     * rouvrent les parcours en arrivant, et gardent l'enquête choisie entre-temps.
     *
     * Tant que les cibles ne sont pas lues, rien n'est à résoudre : l'ouverture s'en chargera, en
     * tenant compte de la réserve.
     */
    public readonly restrictToSurveys = (surveysOnly: boolean): Observable<void> => {
        this.surveysOnlySignal.set(surveysOnly);

        return this.loadingSignal() ? of(undefined) : this.retain(this.allOptionsSignal(), null, this.scopeSignal());
    };

    /** Les parcours clients ne sont chargés que si l'abonnement les ouvre. */
    private readonly findOptions = (): Observable<DashboardScopeOption[]> =>
        this.license$.pipe(
            switchMap((license) =>
                forkJoin({
                    customerJourneys: license.licence?.customerJourney ? this.customerJourneyService.findAll() : of([]),
                    surveys: this.surveyService.findAllHeaders(RESTITUABLE_SURVEY_TYPES),
                })
            ),
            map(({customerJourneys, surveys}) => toDashboardScopeOptions(customerJourneys, surveys))
        );

    /**
     * Pose la liste des cibles et celle qu'il faut regarder.
     *
     * La cible est annoncée dans tous les cas — les pages n'affichent rien sans elle — mais n'est
     * réenregistrée que si elle diffère de celle du compte : ni la simple visite d'une page de
     * restitution, ni la relecture de la liste ne valent un choix.
     *
     * Elle est prise parmi les cibles proposées, et non parmi toutes : une page réservée aux
     * enquêtes ne doit pas rouvrir un parcours.
     */
    private readonly retain = (
        options: DashboardScopeOption[],
        fromUrl: DashboardScope | null,
        working: DashboardScope | null
    ): Observable<void> => {
        this.allOptionsSignal.set(options);

        const scope = resolveDashboardScope(this.options(), fromUrl, working);
        this.announceScope(scope);

        return isSameDashboardScope(scope, working) ? of(undefined) : this.repository.saveWorkingScope(scope);
    };
}
